import React from 'react'
import { NavLink, useRouteMatch } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import MatchRoute from 'route-parser'

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from '@/components/ui/sidebar'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'

import Route from 'route-parser'

import logo from '../assets/img/logo_inline.png'
import logoIcon from '../assets/img/logo_icon.png'
import routes from '../routes/index'
import { useUser } from '../hooks'
import { Query } from '../api'
import ProjectSelector from './ProjectSelector'
import { useMyPermissions } from '../hooks/useMyPermissions'
import { navRightsOf } from '../lib/navRights'
import FeedbackButton from './feedback/FeedbackButton'

/** Check if current URL matches a route pattern (supports :param placeholders) */
const useIsActive = (pattern) => {
  const match = useRouteMatch()
  const { data } = useQuery(Query.ME)
  const url = match.url

  // Try exact pattern match first (handles :projectId etc.)
  const route = new Route(pattern)
  if (route.match(url)) return true

  // Resolve the pattern with actual params for prefix matching
  const projectId = data && data.me.currentProject ? data.me.currentProject.id : ''
  const resolvedPattern = new MatchRoute(pattern).reverse({ projectId }) || pattern

  const normalizedPattern = resolvedPattern.replace(/\/$/, '')
  const normalizedUrl = url.replace(/\/$/, '')
  return normalizedUrl === normalizedPattern || normalizedUrl.startsWith(normalizedPattern + '/')
}

const SidebarNavItem = ({ name, icon: Icon, to, badge }) => {
  const isActive = useIsActive(to)
  const { data } = useQuery(Query.ME)

  const resolvedPath = new MatchRoute(to).reverse({
    projectId: data && data.me.currentProject ? data.me.currentProject.id : '',
  })

  if (!resolvedPath) return null

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive} tooltip={name}>
        <NavLink to={resolvedPath}>
          {Icon && <Icon size={18} />}
          <span>{name}</span>
          {badge && <Badge className="ml-auto group-data-[collapsible=icon]:hidden">{badge}</Badge>}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

const SidebarNavSubItem = ({ name, to }) => {
  const isActive = useIsActive(to)
  const { data } = useQuery(Query.ME)

  const resolvedPath = new MatchRoute(to).reverse({
    projectId: data && data.me.currentProject ? data.me.currentProject.id : '',
  })

  if (!resolvedPath) return null

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild isActive={isActive}>
        <NavLink to={resolvedPath}>
          <span>{name}</span>
        </NavLink>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  )
}

const SidebarNavCategory = ({ name, icon: Icon, to, children }) => {
  const isActive = useIsActive(to)

  return (
    <Collapsible asChild defaultOpen={isActive} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={name}>
            {Icon && <Icon size={18} />}
            <span>{name}</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {children}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

const AppSidebar = (props) => {
  const { t } = useTranslation()
  const user = useUser()
  const { data: meData } = useQuery(Query.ME)
  const currentProjectId = meData?.me?.currentProject?.id
  const { perms, loading: permsLoading, can, recordScoped } = useMyPermissions(currentProjectId)
  const navRights = navRightsOf(user.rights)

  // A module-less item is always shown. While permissions load we fail open
  // (show), since the backend still enforces and route guards redirect anyway.
  // Routes may require a stronger action than VIEW via `moduleAction`
  // (e.g. project settings = PROJECT.EDIT).
  const canSeeModule = (module, action) => {
    if (!module) return true
    if (permsLoading || !perms) return true
    const needed = action || 'VIEW'
    // A contractor's representative sees Cost Control with his contracts only (1b-B spec §8).
    return can(needed, module) || (needed === 'VIEW' && recordScoped(module))
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="group-data-[collapsible=icon]:px-2 px-6">
        <a href="/" className="flex items-center py-2">
          <img src={logoIcon} className="size-6 rounded shrink-0 hidden group-data-[collapsible=icon]:block" alt="Urkla" />
          <img src={logo} className="h-5 group-data-[collapsible=icon]:hidden" alt="Urkla" />
        </a>
        <div className="group-data-[collapsible=icon]:hidden pb-1">
          <ProjectSelector />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup className="group-data-[collapsible=icon]:px-2 px-6">
          <SidebarMenu>
            {routes
              .filter((r) => !r.hidden)
              .filter((r) => !r.allowedRoles || r.allowedRoles.includes(navRights))
              .filter(
                (r) => !r.allowedEmails || !user?.email || r.allowedEmails.includes(user.email),
              )
              .map((category, index) => {
                // The category's own page and its children gate independently:
                // a user may keep a child (e.g. snags) while losing the landing
                // page (e.g. project settings behind PROJECT.EDIT).
                const ownPageVisible = canSeeModule(category.module, category.moduleAction)
                const filteredChildren = category.children
                  ? category.children
                      .filter((r) => !r.hidden)
                      .filter((r) => !r.allowedRoles || r.allowedRoles.includes(navRights))
                      .filter(
                        (r) =>
                          !r.allowedEmails || !user?.email || r.allowedEmails.includes(user.email),
                      )
                      .filter((r) => canSeeModule(r.module, r.moduleAction))
                  : []

                if (filteredChildren.length > 0) {
                  return (
                    <SidebarNavCategory
                      key={index}
                      name={t(category.name)}
                      icon={category.icon}
                      to={ownPageVisible ? category.path : filteredChildren[0].path}
                    >
                      {/* The category label is a collapse trigger, not a link — without
                          this item the category's own page (e.g. project settings for
                          delegated project admins) is unreachable from the UI. Only for
                          categories that actually have an own page (component). */}
                      {ownPageVisible && category.component && (
                        <SidebarNavSubItem
                          name={t('Route_category_own_page', 'Settings')}
                          to={category.path}
                        />
                      )}
                      {filteredChildren.map((route, childIndex) => (
                        <SidebarNavSubItem
                          key={childIndex}
                          name={t(route.name)}
                          to={route.path}
                        />
                      ))}
                    </SidebarNavCategory>
                  )
                }

                if (!ownPageVisible) return null

                return (
                  <SidebarNavItem
                    key={index}
                    name={t(category.name)}
                    to={category.path}
                    icon={category.icon}
                    badge={category.beta ? t('Common_beta') : null}
                  />
                )
              })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="group-data-[collapsible=icon]:px-2 px-6">
        <FeedbackButton />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}

export default AppSidebar
